"use client";

import { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

// import { labels, priorities, statuses } from "../data/data";
import { DataTableColumnHeader } from "../../../components/shared/data-table/data-table-column-header";

// Helper to highlight matching text
const highlightMatch = (text: string | null | undefined, searchTerm: string) => {
  if (!text || !searchTerm) return text || "";
  const lowerText = text.toLowerCase();
  const lowerSearch = searchTerm.toLowerCase();
  const index = lowerText.indexOf(lowerSearch);
  if (index === -1) return text;

  return (
    <>
      {text.slice(0, index)}
      <mark className="bg-yellow-200 rounded px-0.5">{text.slice(index, index + searchTerm.length)}</mark>
      {text.slice(index + searchTerm.length)}
    </>
  );
};

//import { DataTableRowActions } from "./data-table-row-actions";

// account_code
// role
// price_listId
// price_list {
//   name
// }
// customer_groupId
// customer_group {
//   name
// }
// language
// is_b2b_user
// id
// name
// email
// b2b_company_address {
//   country
// }

export const columns: any[] = [
  // {
  //   id: "select",
  //   header: ({ table }) => (
  //     <Checkbox
  //       checked={
  //         table.getIsAllPageRowsSelected() ||
  //         (table.getIsSomePageRowsSelected() && "indeterminate")
  //       }
  //       onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
  //       aria-label="Select all"
  //       className="translate-y-[2px]"
  //     />
  //   ),
  //   cell: ({ row }) => (
  //     <Checkbox
  //       checked={row.getIsSelected()}
  //       onCheckedChange={(value) => row.toggleSelected(!!value)}
  //       aria-label="Select row"
  //       className="translate-y-[2px]"
  //     />
  //   ),
  //   enableSorting: false,
  //   enableHiding: false,
  // },
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Id" />
    ),
    cell: ({ row }) => (
      <div className="text-xs text-muted-foreground">{row.getValue("id")}</div>
    ),
    enableSorting: false,
    enableHiding: true,
    meta: { width: "50px" },
  },
  {
    accessorKey: "code",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Code" />
    ),
    cell: ({ row }) => {
      const searchTerm = (row.original as any).searchTerm || "";
      const code = row.getValue("code") as string;
      return (
        <div className="font-mono text-sm whitespace-nowrap">
          {highlightMatch(code, searchTerm)}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
    // 180px fits the longest code in the catalogue — ZM-PERFECTHARMONY and
    // ZM-OVERTHEHORIZON, 17 monospace chars at text-sm — with room to spare.
    // `whitespace-nowrap` above is the part that actually guarantees it: this
    // table uses auto layout, where `meta.width` is only a hint the browser is
    // free to shrink, which is why the old 100px wrapped these onto two lines
    // rather than widening the column.
    meta: { width: "180px" },
  },
  {
    accessorKey: "name",
    id: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    enableSorting: false,
    enableHiding: true,

    cell: ({ row }) => {
      const matchedFabrics = (row.original as any).matchedFabrics || [];
      const searchTerm = (row.original as any).searchTerm || "";
      const displayFabrics = matchedFabrics.slice(0, 5);
      const remaining = matchedFabrics.length - 5;
      const name = row.getValue("name") as string;

      return (
        <div>
          <div className="max-w-[500px] truncate font-medium">
            {highlightMatch(name, searchTerm)}
          </div>
          {displayFabrics.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {displayFabrics.map((f: any) => (
                <span
                  key={f.id}
                  className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs"
                >
                  {f.image?.src && (
                    <img
                      src={f.image.src}
                      alt=""
                      className="h-4 w-4 rounded object-cover"
                    />
                  )}
                  {f.code && <span className="font-medium">{highlightMatch(f.code, searchTerm)}</span>}
                  {f.code && f.color_name && <span className="text-gray-400">·</span>}
                  {f.color_name && <span>{highlightMatch(f.color_name, searchTerm)}</span>}
                </span>
              ))}
              {remaining > 0 && (
                <span className="text-xs text-gray-500">+{remaining} more</span>
              )}
            </div>
          )}
        </div>
      );
    },
  },

  {
    accessorKey: "type",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    meta: { width: "100px" },
  },

  {
    id: "fabric_price_category",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Category" />
    ),
    enableSorting: false,
    enableHiding: true,
    meta: { width: "150px" },
    cell: ({ row }) => {
      const categories = (row.original as any).fabric_price_category;
      const manufacturerId = (row.original as any).manufacturerId;
      if (!categories || categories.length === 0) return null;
      const filtered = categories.filter(
        (cat: any) => cat.manufacturerId === manufacturerId
      );
      if (filtered.length === 0) return null;
      const sorted = [...filtered].sort(
        (a: any, b: any) => a.group_number - b.group_number
      );
      return (
        <div className="flex flex-wrap gap-1">
          {sorted.map((cat: any) => (
            <Badge key={cat.id} variant="secondary" className="text-xs">
              CAT {cat.group_number}
            </Badge>
          ))}
        </div>
      );
    },
  },

  // {
  //   accessorKey: "customer_group.name",
  //   id: "customer_group.name",
  //   header: ({ column }) => (
  //     <DataTableColumnHeader column={column} title="Group" />
  //   ),
  //   enableSorting: true,
  //   enableHiding: true,
  //   id: "customer_group.name",
  //   cell: ({ row }) => {
  //     if (!row.getValue("customer_group.name")) {
  //       return null;
  //     }
  //     return (
  //       <Badge variant="secondary" className="text-xs">
  //         {row.getValue("customer_group.name")}
  //       </Badge>
  //     );
  //   },
  // },
  // {
  //   accessorKey: "price_list.name",
  //   id: "price_list.name",
  //   header: ({ column }) => (
  //     <DataTableColumnHeader column={column} title="Price List" />
  //   ),
  //   cell: ({ row }) => {
  //     if (!row.getValue("price_list.name")) {
  //       return null;
  //     }
  //     return (
  //       <div className="flex min-w-[100px] ">
  //         {" "}
  //         <Badge variant="secondary" className="text-xs">
  //           {row.getValue("price_list.name")}
  //         </Badge>
  //       </div>
  //     );
  //   },
  //   enableSorting: true,
  //   enableHiding: true,
  //   enableResizing: false,
  //   minSize: 200,
  //   size: 200,
  //   maxSize: 200,
  //   enableMultiSort: true,
  // },
];
